import { useCallback, useMemo } from 'react';
import { useStateRef } from './use-state-ref';
import { uniq } from 'lodash';

export type Pool<T> = Record<string, PoolItem<T>>;
export interface PoolItem<T> {
  data: T;
}

/**
 * A custom hook to provide a simple qool implementation
 */
export const usePool = <T>() => {
  const [activeItems, setActiveItems, activeItemsRef] = useStateRef<Pool<T>>({});
  const [pool, setPool, poolRef] = useStateRef<Pool<T>>({});

  const itemIds = useMemo(() => {
    return uniq([...Object.keys(activeItems), ...Object.keys(pool)]);
  }, [activeItems, pool]);

  /**
   * Adds an item to the pool. This will not affect the current item. If an item with the given
   * id is already in the pool, that item will be replaced (while keeping its position in the pool).
   * @param id      - the id of the item to add to the pool
   * @param data    - the data for the item
   */
  const addToPool = useCallback(
    (id: string, data: T) => {
      setPool({ ...poolRef.current, [id]: { data } });
    },
    [poolRef, setPool]
  );

  /**
   * Will move the next item out of the pool, set it as an active item and return it
   * @param id
   */
  const setActiveItem = useCallback(
    (id: string) => {
      const item = poolRef.current[id];

      if (item) {
        setActiveItems({ ...activeItemsRef.current, [id]: item });
        const newPool = { ...poolRef.current };
        delete newPool[id];
        setPool(newPool);
      }

      return item;
    },
    [activeItemsRef, poolRef, setActiveItems, setPool]
  );

  /**
   * Removes the corresponding item from the active items pool
   */
  const removeActiveItem = useCallback(
    (id: string) => {
      const item = activeItemsRef.current[id];

      if (activeItemsRef.current[id]) {
        const newActiveItems = { ...activeItemsRef.current };
        delete newActiveItems[id];
        setActiveItems(newActiveItems);
      }

      return item;
    },
    [activeItemsRef, setActiveItems]
  );

  /**
   * Removes an item from the pool. This will not affect the current item.
   */
  const removeFromPool = useCallback(
    (id: string) => {
      const item = poolRef.current[id];

      if (poolRef.current[id]) {
        const newPoolItems = { ...poolRef.current };
        delete newPoolItems[id];
        setPool(newPoolItems);
      }

      return item;
    },
    [poolRef, setPool]
  );

  return useMemo(
    () => ({
      addToPool,
      activeItems,
      activeItemsRef,
      isEmpty: !Object.keys(poolRef.current).length && !Object.keys(activeItemsRef.current).length,
      itemIds,
      pool,
      poolRef,
      removeActiveItem,
      removeFromPool,
      setActiveItem
    }),
    [activeItems, activeItemsRef, addToPool, itemIds, pool, poolRef, removeActiveItem, removeFromPool, setActiveItem]
  );
};
